// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { type Node } from 'react';

import AccountActions from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalTopbar/AccountActions';
import { AccountsMenuContainer } from '@zapier/design-system-patterns/src/components/UniversalNav/AccountsMenu';
import { AsyncIndicator } from '@zapier/design-system';

import { type UniversalTopbarProps } from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalTopbar';

type AccountMenuRendererProps = {
  accountSwitcherUrl?: string,
  currentAccountId?: number,
  currentUrl: string,
  isLoadingLoggedIn: boolean,
  isLoggedIn: boolean,
  loginHref: string,
  signupHref: string,
};

type ShouldRender = AccountMenuRendererProps => boolean;

// This is a function that *makes* a render function. The reason for
// that is so the render function has access to `AccountMenuRendererProps`
// which is necessary in some circumstances.
type MakeRenderAccountMenu = AccountMenuRendererProps => any => Node;

type RendererConfig = {
  shouldRender: ShouldRender,
  makeRenderer: MakeRenderAccountMenu,
};

const renderAccountsMenuContainerConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.isLoggedIn;
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    return (
      <AccountsMenuContainer
        accountSwitcherUrl={renderProps.accountSwitcherUrl || ''}
        currentAccountId={renderProps.currentAccountId}
        size={props.size}
      />
    );
  },
};

const renderLoadingConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return !renderProps.isLoggedIn && renderProps.isLoadingLoggedIn;
  },
  makeRenderer: () => () => {
    return <AsyncIndicator ariaLabel="Loading accounts" />;
  },
};

const renderSignUpOnlyConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/login');
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    return (
      <AccountActions
        selectedHref={renderProps.currentUrl}
        shouldRenderSignIn={false}
        shouldRenderSignUp={true}
        signupHref={renderProps.signupHref}
        size={props.size}
      />
    );
  },
};

const renderSignInOnlyConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/sign-up');
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    return (
      <AccountActions
        loginHref={renderProps.loginHref}
        selectedHref={renderProps.currentUrl}
        shouldRenderSignIn={true}
        shouldRenderSignUp={false}
        size={props.size}
      />
    );
  },
};

const renderOnboardingConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/onboarding');
  },
  makeRenderer: () => () => {
    return null;
  },
};

// Order-sensitive array of configurations to render the account menu.
// The first one whose `shouldRender` returns `true` is used.
const configs: Array<RendererConfig> = [
  renderSignUpOnlyConfig,
  renderSignInOnlyConfig,
  renderOnboardingConfig,
  renderAccountsMenuContainerConfig,
  renderLoadingConfig,
];

// This function helps figure out what to render for the accounts menu.
// There are numerous conditions for rendering the accounts menu, and
// instead of passing all of those renderProps into `AccountsMenu` and making
// that component complex and have access to renderProps that it probably
// shouldn't, handle all of that complexity in render functions defined
// within this file.
const findAccountsMenuRenderer = (renderProps: AccountMenuRendererProps) => {
  const foundConfig = configs.find((config: RendererConfig) => {
    return config.shouldRender(renderProps);
  });
  return foundConfig && foundConfig.makeRenderer(renderProps);
};

export default findAccountsMenuRenderer;
